import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { MazeGrid } from "./MazeGrid";
import { MazeRunner } from "./MazeRunner";
import { ProgramEditor } from "./ProgramEditor";
import {
  executeProgramAsync,
  calculateStars,
  createInitialState,
  type MazeConfig,
  type ProgramState,
  type ExecutionResult,
  type ExecutionSpeed,
} from "./ProgramExecutor";
import type {
  MazeGameProps,
  CodeBlockData,
  BlockType,
  Position,
  Direction,
  Collectible,
} from "./types";

/**
 * Default execution speed
 */
const DEFAULT_SPEED: ExecutionSpeed = "normal";

/**
 * MazeGame Component
 *
 * The complete programming challenge experience that combines the maze,
 * robot runner, and visual programming editor. Manages game state and
 * orchestrates execution with animations.
 *
 * @example
 * <MazeGame
 *   size={[5, 5]}
 *   start={[0, 0]}
 *   end={[4, 4]}
 *   obstacles={[[1, 1], [2, 2]]}
 *   collectibles={[{ position: [2, 0], type: 'star' }]}
 *   onComplete={(stats) => console.log('Completed!', stats)}
 * />
 */
export function MazeGame({
  size,
  start,
  end,
  obstacles = [],
  collectibles: initialCollectibles = [],
  onComplete,
  onAnswer,
  isSubmitted = false,
  isCorrect = false,
  discipline = "programming",
}: MazeGameProps) {
  // Program state
  const [blocks, setBlocks] = useState<CodeBlockData[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [hasWon, setHasWon] = useState(false);
  const [executingBlockId, setExecutingBlockId] = useState<string | null>(null);

  // Runner state
  const [runnerPosition, setRunnerPosition] = useState<Position>([...start]);
  const [runnerDirection, setRunnerDirection] = useState<Direction>("right");
  const [isMoving, setIsMoving] = useState(false);
  const [isCollecting, setIsCollecting] = useState(false);

  // Collectibles state
  const [collectibles, setCollectibles] = useState<Collectible[]>(() =>
    initialCollectibles.map((c, index) => ({
      position: c.position,
      type: c.type,
      collected: false,
    }))
  );

  // Reference for cell size (passed to runner)
  const [cellSize, setCellSize] = useState(80);
  const gridGap = 8;

  // Execution ref for cancellation
  const executionRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // Build maze config for executor
  const mazeConfig = useMemo<MazeConfig>(
    () => ({
      size,
      start,
      end,
      obstacles,
      collectibles: initialCollectibles.map((c, index) => ({
        position: c.position,
        type: c.type,
        id: `collectible-${index}`,
      })),
      optimalMoves: Math.abs(end[0] - start[0]) + Math.abs(end[1] - start[1]),
    }),
    [size, start, end, obstacles, initialCollectibles]
  );

  // Determine available blocks based on level difficulty
  const availableBlocks = useMemo<BlockType[]>(() => {
    // For now, always provide basic directional blocks
    // Future: Could be passed as prop or determined by level
    return ["move_up", "move_down", "move_left", "move_right"];
  }, []);

  // Reset the game to initial state
  const handleReset = useCallback(() => {
    // Cancel any running execution
    executionRef.current.cancelled = true;
    executionRef.current = { cancelled: false };

    // Reset all state
    setIsExecuting(false);
    setHasWon(false);
    setExecutingBlockId(null);
    setRunnerPosition([...start]);
    setRunnerDirection("right");
    setIsMoving(false);
    setIsCollecting(false);
    setCollectibles(
      initialCollectibles.map((c, index) => ({
        position: c.position,
        type: c.type,
        collected: false,
      }))
    );
    // Don't reset blocks - let user keep their program
  }, [start, initialCollectibles]);

  // Execute the program
  const handlePlay = useCallback(async () => {
    if (blocks.length === 0 || isExecuting) return;

    // Reset state for new execution
    executionRef.current = { cancelled: false };
    setIsExecuting(true);
    setHasWon(false);
    setRunnerPosition([...start]);
    setRunnerDirection("right");
    setCollectibles(
      initialCollectibles.map((c, index) => ({
        position: c.position,
        type: c.type,
        collected: false,
      }))
    );

    try {
      const result = await executeProgramAsync(
        blocks,
        mazeConfig,
        (state: ProgramState, justCollected: boolean) => {
          // Check if cancelled
          if (executionRef.current.cancelled) return;

          // Update runner state
          setRunnerPosition(state.position);
          setRunnerDirection(state.direction);
          setExecutingBlockId(state.currentBlockId);

          // Trigger movement animation
          setIsMoving(true);
          setTimeout(() => {
            if (!executionRef.current.cancelled) {
              setIsMoving(false);
            }
          }, 200);

          // Handle collection animation
          if (justCollected) {
            setIsCollecting(true);
            setCollectibles((prev) =>
              prev.map((c) => {
                const posKey = `${c.position[0]},${c.position[1]}`;
                const stateKey = `${state.position[0]},${state.position[1]}`;
                if (posKey === stateKey && !c.collected) {
                  return { ...c, collected: true };
                }
                return c;
              })
            );
            setTimeout(() => {
              if (!executionRef.current.cancelled) {
                setIsCollecting(false);
              }
            }, 400);
          }

          // Check for victory
          if (state.success) {
            setHasWon(true);
          }
        },
        DEFAULT_SPEED
      );

      // Execution complete
      if (!executionRef.current.cancelled) {
        setIsExecuting(false);
        setExecutingBlockId(null);

        // Calculate stars and notify parent
        const stars = calculateStars(result);

        if (onAnswer) {
          // Convert blocks to direction commands for validation
          const commands = blocks
            .map((b) => {
              switch (b.type) {
                case "move_up":
                  return "up";
                case "move_down":
                  return "down";
                case "move_left":
                  return "left";
                case "move_right":
                  return "right";
                default:
                  return null;
              }
            })
            .filter((c): c is Direction => c !== null);
          onAnswer(commands);
        }

        if (onComplete && result.success) {
          onComplete({
            starsCollected: result.collected.length,
            moves: result.steps,
          });
        }
      }
    } catch (error) {
      // Handle execution errors gracefully
      console.error("Execution error:", error);
      setIsExecuting(false);
      setExecutingBlockId(null);
    }
  }, [blocks, isExecuting, start, initialCollectibles, mazeConfig, onAnswer, onComplete]);

  // Measure cell size from the grid
  useEffect(() => {
    const gridElement = document.querySelector('[role="grid"]');
    if (gridElement) {
      const firstCell = gridElement.querySelector('[role="gridcell"]');
      if (firstCell) {
        const rect = firstCell.getBoundingClientRect();
        setCellSize(rect.width);
      }
    }
  }, [size]);

  return (
    <div className="flex flex-col gap-8 w-full max-w-5xl mx-auto">
      {/* Game header with character greeting */}
      <div className="flex items-center gap-4 px-4">
        <div className="text-4xl">🦉</div>
        <div>
          <h2 className="font-display font-bold text-2xl text-pixel-dark">
            Help me reach the goal!
          </h2>
          <p className="font-body text-neutral-600">
            Drag the command blocks to build a program, then press Run!
          </p>
        </div>
      </div>

      {/* Main game area */}
      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        {/* Maze area */}
        <div className="flex items-center justify-center">
          <MazeGrid
            size={size}
            start={start}
            end={end}
            obstacles={obstacles}
            collectibles={collectibles}
            isExecuting={isExecuting}
            discipline={discipline}
          >
            <MazeRunner
              position={runnerPosition}
              direction={runnerDirection}
              isMoving={isMoving}
              isCollecting={isCollecting}
              hasWon={hasWon}
              cellSize={cellSize}
              gridGap={gridGap}
            />
          </MazeGrid>
        </div>

        {/* Programming editor */}
        <div>
          <ProgramEditor
            blocks={blocks}
            onBlocksChange={setBlocks}
            onPlay={handlePlay}
            onReset={handleReset}
            isExecuting={isExecuting}
            availableBlocks={availableBlocks}
            maxBlocks={15}
            hasWon={hasWon}
            executingBlockId={executingBlockId}
          />
        </div>
      </div>

      {/* Result feedback */}
      {!isExecuting && hasWon && (
        <div
          className="
            flex items-center justify-center gap-4
            py-6 px-8
            bg-gradient-to-r from-meadow-100 via-meadow-50 to-meadow-100
            rounded-3xl
            border-4 border-meadow-300
            shadow-[0_4px_16px_rgba(107,203,119,0.3)]
          "
        >
          <span className="text-4xl animate-bounce">🎉</span>
          <div className="text-center">
            <p className="font-display font-bold text-2xl text-meadow-700">
              Fantastic!
            </p>
            <p className="font-body text-meadow-600">
              You helped Pixel reach the goal!
            </p>
          </div>
          <span className="text-4xl animate-bounce" style={{ animationDelay: "0.1s" }}>
            🎊
          </span>
        </div>
      )}

      {/* Failure feedback (hit obstacle) */}
      {!isExecuting && !hasWon && blocks.length > 0 && executingBlockId === null && runnerPosition[0] !== start[0] && runnerPosition[1] !== start[1] && (
        <div
          className="
            flex items-center justify-center gap-4
            py-6 px-8
            bg-gradient-to-r from-fairy-100 via-fairy-50 to-fairy-100
            rounded-3xl
            border-4 border-fairy-300
            shadow-[0_4px_16px_rgba(255,107,157,0.3)]
          "
        >
          <span className="text-4xl">🤔</span>
          <div className="text-center">
            <p className="font-display font-bold text-2xl text-fairy-700">
              Almost there!
            </p>
            <p className="font-body text-fairy-600">
              Try adjusting your program and run it again!
            </p>
          </div>
          <span className="text-4xl">💪</span>
        </div>
      )}
    </div>
  );
}

export default MazeGame;
